import React, { useState } from 'react';
import { createFeedbackTopic } from '../../services/feedbackApi';
import type { FeedbackCategory } from '../../types';

interface CreateFeedbackModalProps {
  categories: FeedbackCategory[];
  onClose: () => void;
  onSuccess: () => void;
}

const CreateFeedbackModal: React.FC<CreateFeedbackModalProps> = ({
  categories,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category_id: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // 清除对应字段的错误
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.title.trim()) {
      newErrors.title = '请输入反馈标题';
    } else if (formData.title.trim().length < 5) {
      newErrors.title = '标题至少需要5个字符';
    } else if (formData.title.trim().length > 200) {
      newErrors.title = '标题不能超过200个字符';
    }
    
    if (!formData.content.trim()) {
      newErrors.content = '请输入反馈内容';
    } else if (formData.content.trim().length < 10) {
      newErrors.content = '内容至少需要10个字符';
    }
    
    if (!formData.category_id) {
      newErrors.category_id = '请选择反馈分类';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    try {
      await createFeedbackTopic({
        title: formData.title.trim(),
        content: formData.content.trim(),
        category_id: parseInt(formData.category_id)
      });
      
      onSuccess();
    } catch (err) {
      console.error('创建反馈失败:', err);
      alert('创建反馈失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  return (
    <div className="modal show d-block create-feedback-modal" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="fas fa-plus-circle me-2 text-primary"></i>
              发布反馈
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={handleClose}
              disabled={loading}
            ></button>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="mb-3">
                <label htmlFor="feedbackTitle" className="form-label">
                  反馈标题 <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.title ? 'is-invalid' : ''}`}
                  id="feedbackTitle"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="请简要描述您的问题或建议"
                  maxLength={200}
                  disabled={loading}
                />
                {errors.title && (
                  <div className="invalid-feedback">
                    {errors.title}
                  </div>
                )}
                <div className="form-text">
                  {formData.title.length}/200 字符
                </div>
              </div>

              <div className="mb-3">
                <label htmlFor="feedbackCategory" className="form-label">
                  反馈分类 <span className="text-danger">*</span>
                </label>
                <select
                  className={`form-select ${errors.category_id ? 'is-invalid' : ''}`}
                  id="feedbackCategory"
                  name="category_id"
                  value={formData.category_id}
                  onChange={handleInputChange}
                  disabled={loading}
                >
                  <option value="">请选择分类</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
                {errors.category_id && (
                  <div className="invalid-feedback">
                    {errors.category_id}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <label htmlFor="feedbackContent" className="form-label">
                  详细描述 <span className="text-danger">*</span>
                </label>
                <textarea
                  className={`form-control ${errors.content ? 'is-invalid' : ''}`}
                  id="feedbackContent"
                  name="content"
                  rows={6}
                  value={formData.content}
                  onChange={handleInputChange}
                  placeholder="请详细描述您遇到的问题或建议，包括：&#10;1. 具体的操作步骤&#10;2. 期望的结果&#10;3. 实际的结果&#10;4. 其他相关信息"
                  disabled={loading}
                />
                {errors.content && (
                  <div className="invalid-feedback">
                    {errors.content}
                  </div>
                )}
                <div className="form-text">
                  {formData.content.length} 字符，至少需要10个字符
                </div>
              </div>

              {/* 分类说明 */}
              <div className="alert alert-info">
                <h6 className="alert-heading">
                  <i className="fas fa-info-circle me-2"></i>
                  分类说明
                </h6>
                <div className="row">
                  {categories.map(category => (
                    <div key={category.id} className="col-md-6 mb-2">
                      <span className={`badge bg-${category.color} me-2`}>
                        <i className={`${category.icon} me-1`}></i>
                        {category.name}
                      </span>
                      <small className="text-muted">
                        {category.name === 'Bug报告' && '功能异常、错误问题'}
                        {category.name === '功能建议' && '新功能需求、改进建议'}
                        {category.name === '使用问题' && '操作疑问、使用帮助'}
                        {category.name === '性能优化' && '速度、体验优化建议'}
                        {category.name === '界面优化' && 'UI/UX改进建议'}
                        {category.name === '其他' && '其他类型反馈'}
                      </small>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleClose}
                disabled={loading}
              >
                取消
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    提交中...
                  </>
                ) : (
                  <>
                    <i className="fas fa-paper-plane me-2"></i>
                    提交反馈
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateFeedbackModal;
